import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  Box,
} from '@mui/material';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Form, Formik } from 'formik';
import useCheckPermission from '../../hooks/useCheckPermission';
import HtmlAppletHelperDocs from './HtmlAppletHelperDocs';
import HtmlAppletPreviewDialog from './HtmlAppletPreviewDialog';
import HtmlAppletResultsPanel from './HtmlAppletResultsPanel';

const HtmlAppletForm = (props: any) => {
  const {
    entity,
    onSubmit,
    errors,
    handleCancel,
    readonly = false,
  } = props;

  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );
  const { checkPermission } = useCheckPermission();
  const [helperOpen, setHelperOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const canViewContent = checkPermission(['ViewHtmlApplet']);
  const canViewResults = checkPermission(['ViewHtmlAppletResults']);
  const isEdit = !!entity?.id;
  return (
    <Formik
      initialValues={{
        ...entity,
        htmlContent: entity.htmlContent ?? '',
        file: null as File | null,
        responsibilityAccepted: false,
      }}
      enableReinitialize={true}
      validate={(values) => {
        const validationErrors: any = {};
        if (!values.name) {
          validationErrors.name = 'Kötelező';
        }
        if (!values.responsibilityAccepted && !readonly) {
          validationErrors.responsibilityAccepted = 'Kötelező';
        }
        const hasHtml =
          values.htmlContent != null &&
          String(values.htmlContent).trim() !== '';
        const hasFile = !!values.file;
        if (!readonly && !isEdit && !hasHtml && !hasFile) {
          validationErrors.htmlContent =
            'HTML tartalom vagy fájl megadása kötelező';
        }
        return validationErrors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values, setSubmitting);
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        setFieldValue,
        handleChange,
        errors: validationErrors,
      }) => (
        <Form>
          <Paper>
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "left",
                p: 5
              }}>
              <Grid size={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1
                  }}>
                  <h2 style={{ margin: 0 }}>
                    HTML applet {isEdit ? 'szerkesztés' : 'létrehozás'}
                  </h2>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5
                    }}>
                    <Tooltip title="Előnézet">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() => setPreviewOpen(true)}
                          aria-label="Előnézet"
                          disabled={
                            !(values.htmlContent || '').trim() &&
                            !values.file &&
                            !values.contentUrl
                          }
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Súgó">
                      <IconButton
                        color="primary"
                        onClick={() => setHelperOpen(true)}
                        aria-label="Súgó"
                      >
                        <HelpOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
              <Grid size={12} sx={{
                pt: 2
              }}>
                <Box sx={{
                  color: "red"
                }}>
                  {errors.map((error: string) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Grid>

              <Grid size={12}>
                <TextField
                  value={values.name}
                  onChange={handleChange}
                  label="Név"
                  name="name"
                  required
                  disabled={readonly}
                  fullWidth
                  autoFocus
                  error={!!touched.name && !!validationErrors.name}
                  helperText={
                    !!touched.name &&
                    !!validationErrors.name &&
                    (validationErrors.name as string)
                  }
                />
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                  label="Elfogadom, hogy az applet tartalmáért felelek"
                  control={
                    <Checkbox
                      checked={!!values.responsibilityAccepted}
                      disabled={readonly}
                      onChange={(e) => {
                        setFieldValue(
                          'responsibilityAccepted',
                          e.target.checked
                        );
                      }}
                    />
                  }
                />
                {!!touched.responsibilityAccepted &&
                  !!validationErrors.responsibilityAccepted && (
                    <Typography color="error" variant="caption" sx={{
                      display: "block"
                    }}>
                      {validationErrors.responsibilityAccepted as string}
                    </Typography>
                  )}
              </Grid>

              <Grid size={12}>
                <TextField
                  value={values.htmlContent}
                  onChange={handleChange}
                  label="HTML tartalom"
                  name="htmlContent"
                  disabled={readonly}
                  fullWidth
                  multiline
                  minRows={8}
                  error={
                    !!touched.htmlContent && !!validationErrors.htmlContent
                  }
                  helperText={
                    (!!touched.htmlContent &&
                      !!validationErrors.htmlContent &&
                      (validationErrors.htmlContent as string)) ||
                    'Inline HTML, vagy válasszon fájlt alább'
                  }
                />
              </Grid>

              <Grid size={12}>
                <Button variant="outlined" component="label" disabled={readonly}>
                  Fájl kiválasztása (.html, .zip)
                  <input
                    type="file"
                    hidden
                    accept=".html,.zip"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      setFieldValue('file', file);
                    }}
                  />
                </Button>
                <Button
                  variant="outlined"
                  sx={{ ml: 1 }}
                  startIcon={<VisibilityIcon />}
                  disabled={
                    !(values.htmlContent || '').trim() &&
                    !values.file &&
                    !values.contentUrl
                  }
                  onClick={() => setPreviewOpen(true)}
                >
                  Előnézet
                </Button>
                {values.file && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Kiválasztott fájl: {values.file.name}
                  </Typography>
                )}
              </Grid>

              {isEdit && canViewContent && (
                <Grid size={12}>
                  {values.contentUrl && (
                    <Typography variant="body2">
                      Tartalom:{' '}
                      <Link href={values.contentUrl} target="_blank" rel="noreferrer">
                        {values.contentUrl}
                      </Link>
                    </Typography>
                  )}
                  {values.packageUrl && (
                    <Typography variant="body2">
                      Csomag:{' '}
                      <Link href={values.packageUrl} target="_blank" rel="noreferrer">
                        {values.packageUrl}
                      </Link>
                    </Typography>
                  )}
                </Grid>
              )}

              {isEdit && canViewResults && entity?.id && (
                <Grid size={12}>
                  <Typography variant="h6" gutterBottom>
                    Eredmények
                  </Typography>
                  <HtmlAppletResultsPanel
                    appletId={entity.id}
                    appletName={entity.name}
                    showExport
                    height={420}
                  />
                </Grid>
              )}

              <Grid
                container
                sx={{
                  pt: 3,
                  justifyContent: "left"
                }}>
                {!readonly && (
                  <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{
                    p: 1
                  }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth={isSmallScreen}
                      disabled={isSubmitting}
                    >
                      Mentés
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{
                  p: 1
                }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth={isSmallScreen}
                    onClick={handleCancel}
                  >
                    Mégse
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <HtmlAppletPreviewDialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            appletId={entity?.id}
            name={values.name}
            htmlContent={values.htmlContent}
            file={values.file}
            contentUrl={values.contentUrl}
          />

          <Dialog
            open={helperOpen}
            onClose={() => setHelperOpen(false)}
            maxWidth="md"
            fullWidth
            scroll="paper"
          >
            <DialogTitle>Súgó — Hermész applet dokumentáció</DialogTitle>
            <DialogContent dividers>
              <HtmlAppletHelperDocs />
            </DialogContent>
            <DialogActions>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setHelperOpen(false)}
              >
                Bezárás
              </Button>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};

export default HtmlAppletForm;
